import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Icon, type IconName } from "../../components/ui/Icon";
import { useNative } from "../../../shared/phone/media";
import { PnRow } from "../../../shared/phone/Row";
import { Sheet as PnSheet } from "../../../shared/phone/Sheet";
import { Sheet } from "../../components/ui/Sheet";
import { ccApi, type Runs } from "../../lib/ccApi";
import { runsApi, type RunEvent, type RunInfo } from "../../lib/productsApi";
import { toolName } from "../goal/runEvents";
import { Orb } from "../../components/ui/Orb";
import { hm, parseTime, plural } from "./text";

/** A run as `GET /runs/:id` returns it, with what the owner reads about it. */
export type RunRow = RunInfo & { model: string | null; trigger: string | null; thread_id: string | null; summary_json: string | null };

export interface RunSummary {
  summary?: string[];
  steps?: { title: string; result: string; tools?: string[] }[];
  needs?: string[];
  plan_note?: string | null;
}

export const runSummary = (r: RunRow | null): RunSummary => {
  try {
    return r?.summary_json ? (JSON.parse(r.summary_json) as RunSummary) : {};
  } catch {
    return {};
  }
};

/** A step's icon from the tools it used. */
function stepIcon(tools: string[]): IconName {
  const t = tools.join(" ").toLowerCase();
  if (/propose_action|needs you|held/.test(t)) return "user";
  if (/budget/.test(t)) return "bolt";
  if (/email|smartlead|lead/.test(t)) return "mail";
  if (/result|meta|tracking|ad_/.test(t)) return "eye";
  if (/propose_change|plan/.test(t)) return "doc";
  if (/lesson/.test(t)) return "spark";
  if (/rule/.test(t)) return "flask";
  return "check";
}

const runCache = new Map<string, RunRow>();

export function useRunRow(runId: string | null) {
  const [run, setRun] = useState<RunRow | null>(runId ? (runCache.get(runId) ?? null) : null);
  useEffect(() => {
    if (!runId || runCache.has(runId)) return;
    runsApi.get(runId).then(
      ({ run: r }) => {
        runCache.set(runId, r as RunRow);
        setRun(r as RunRow);
      },
      () => setRun(null),
    );
  }, [runId]);
  return run;
}

/** "9 tasks · 118 steps". */
const weekLine = (w: Runs["lastWeek"]) =>
  `${plural(w.runs, "task")} · ${plural(w.toolCalls, "step")}`;

/** How a run was made: the model, its steps and calls, what it cost, and who checks (the prototype's sheet). */
export function HowMade({ goalId, open, onClose, run, title, events }: { goalId: string; open: boolean; onClose: () => void; run: RunRow; title: string; events: RunEvent[] | null }) {
  const s = runSummary(run);
  const calls = events ? events.filter((e) => e.type === "tool.call").length : null;
  // Why: in the chat a message keeps a transform from its rise, which traps a sheet drawn inside it; the kit's sheet draws over the whole screen.
  const native = useNative();
  const [week, setWeek] = useState<Runs["lastWeek"] | null>(null);
  useEffect(() => {
    if (!open || week) return;
    ccApi.runs(goalId, { limit: 1 }).then((r) => setWeek(r.lastWeek), () => undefined);
  }, [open, week, goalId]);
  if (native)
    return (
      <PnSheet open={open} onClose={onClose} title="How it was made">
        <p className="gp-lede">{title}</p>
        <div className="pn-grp">
          <PnRow title="AI model" value={run.model ?? "The account's default"} />
          <PnRow title={run.kind === "review" ? "This review" : "This task"} value={plural(calls ?? (s.steps ?? []).length, "step")} />
          {week && <PnRow title="This week" value={weekLine(week)} />}
          <PnRow title="Who checks" sub="Flashadz checks your rules before every action, not the AI. Anything that spends or speaks for you waits for your OK." wrap />
        </div>
        <div className="pn-grp gp-g2">
          <PnRow to={`/goals/${encodeURIComponent(goalId)}/log?run=${encodeURIComponent(run.id)}`} onClick={onClose} icon={<Icon name="list" />} iconTone="plain" title="Everything the agent did" />
        </div>
      </PnSheet>
    );
  return (
    <Sheet open={open} onClose={onClose} label="How it was made">
      <div className="eyebrow">{title}</div>
      <h2 style={{ marginTop: 6 }}>How it was made</h2>
      <div className="rows" style={{ marginTop: 14 }}>
        <div className="kv">
          <span>AI model</span>
          <span>{run.model ?? "The account's default model"}</span>
        </div>
        <div className="kv">
          <span>{run.kind === "review" ? "This review" : "This task"}</span>
          <span>
            {plural(calls ?? (s.steps ?? []).length, "step")}
          </span>
        </div>
        {week && (
          <div className="kv">
            <span>This week</span>
            <span>{weekLine(week)}</span>
          </div>
        )}
        <div className="kv">
          <span>Who checks</span>
          <span>Flashadz checks your rules before every action, not the AI. Anything that spends or speaks for you waits for your OK.</span>
        </div>
      </div>
      <div className="m-act" style={{ marginTop: 16 }}>
        <Link className="btn sm ghost" to={`/goals/${encodeURIComponent(goalId)}/log?run=${encodeURIComponent(run.id)}`} onClick={onClose}>
          Open the full log
          <Icon name="right" />
        </Link>
      </div>
    </Sheet>
  );
}

/** The run behind a message ("What I did"): its steps as the agent reported them, folded until opened. */
export function RunBlock({ goalId, runId, title, next }: { goalId: string; runId: string; title: string; next: string | null }) {
  const run = useRunRow(runId);
  const [how, setHow] = useState(false);
  const [events, setEvents] = useState<RunEvent[] | null>(null);
  const s = runSummary(run);
  const steps = s.steps ?? [];
  const head = title.split(" · ")[0] || "What I did";
  const rest = title.split(" · ").slice(1).join(" · ");
  const ended = run?.ended_at ? hm(parseTime(run.ended_at)) : null;
  const name = run?.kind === "review" ? "This morning's review" : run?.kind === "wrapup" ? "The wrap-up" : "The run";

  const openHow = () => {
    setHow(true);
    if (!events) runsApi.events(runId, 0).then((r) => setEvents(r.events), () => setEvents([]));
  };

  return (
    <details className="cc-run">
      <summary>
        <Orb live={false} />
        <span>
          {head}
          {rest ? <em> · {rest}</em> : steps.length ? <em> · {steps.length} steps</em> : null}
        </span>
        <Icon name="chev" />
      </summary>
      <section className="now a-now">
        <div className="now-h">
          <div className="orb-tile">
            <Orb live={false} />
          </div>
          <div className="now-ht">
            <div className="now-t">{name}</div>
            <div className="now-s">{ended ? `Finished ${ended}${run?.kind === "review" && next ? ` · next review ${next}` : ""}` : "Working"}</div>
          </div>
          <span className={`pill ${ended ? "plain" : "live"}`}>{ended ? "Done" : "Working"}</span>
        </div>
        <div className="steps">
          {steps.map((st, i) => (
            <div className="step done" key={i}>
              <span className="st-ic">
                <Icon name={stepIcon(st.tools ?? [])} />
              </span>
              <div className="st-b">
                <div className="st-t">{st.title}</div>
                <div className="st-r">{st.result}</div>
                {(st.tools ?? []).length > 0 && (
                  <div className="st-meta">
                    {(st.tools ?? []).map((t) => (
                      <span className="chip" key={t}>
                        {toolName(t)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <span className="st-time" />
            </div>
          ))}
        </div>
        <div className="now-f">
          <span className="qlinks">
            <button type="button" className="qlink" onClick={openHow}>
              How it was made
            </button>
            <Link className="qlink" to={`/goals/${encodeURIComponent(goalId)}/log?run=${encodeURIComponent(runId)}`}>
              Full log
            </Link>
          </span>
        </div>
      </section>
      {run && <HowMade goalId={goalId} open={how} onClose={() => setHow(false)} run={run} title={name} events={events} />}
    </details>
  );
}
